import type { TFunction } from 'i18next';
import { AlertCircle, Check, Code2, ExternalLink, Globe2, ListTree, X } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { AnalyticsChartRenderer } from '@/modules/analytics/components/AnalyticsChartRenderer';
import {
  getKnownToolDisplayName,
  getToolDisplayName,
} from '@/modules/chat/application/chat-tool-registry';
import { getToolCallProvider } from '@/modules/chat/application/chat-work-log';
import type { ChatToolCall } from '@/modules/chat/application/chat-work-log';
import { ChatToolCallState, ChatToolProvider } from '@/modules/common/types';
import { withProjectPrefix } from '@/modules/project/route-util';

type ToolSummaryField = {
  label: string;
  value: string;
  status: 'Provided' | 'Defaulted' | 'Unset';
  note: string;
};

type ToolCallSummary = {
  headline: string;
  fields: ToolSummaryField[];
};

const TOOL_SUMMARY_STATUS_LABEL_KEYS: Record<ToolSummaryField['status'], string> = {
  Provided: 'modules.mavue2.chat.toolSummary.statuses.provided',
  Defaulted: 'modules.mavue2.chat.toolSummary.statuses.defaulted',
  Unset: 'modules.mavue2.chat.toolSummary.statuses.unset',
};

const ANALYTICS_APPROVAL_TOOL_NAMES = new Set([
  'analytics_dashboard_create',
  'analytics_dashboard_update',
  'analytics_dashboard_delete',
  'analytics_dashboard_members_update',
  'analytics_component_create',
  'analytics_component_update',
  'analytics_component_delete',
  'analytics_components_regenerate',
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return !!value && typeof value === 'object' && !Array.isArray(value);
}

function formatToolValue(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatInlineValue(value: unknown, t: TFunction): string | null {
  if (value === null || value === undefined) {
    return null;
  }

  if (typeof value === 'string') {
    return value.trim().length > 0 ? value : null;
  }

  if (typeof value === 'boolean') {
    return value
      ? t('modules.mavue2.chat.toolSummary.values.yes')
      : t('modules.mavue2.chat.toolSummary.values.no');
  }

  if (typeof value === 'number') {
    return String(value);
  }

  if (Array.isArray(value)) {
    return t('modules.mavue2.chat.toolSummary.values.item', { count: value.length });
  }

  if (isRecord(value)) {
    return t('modules.mavue2.chat.toolSummary.values.field', {
      count: Object.keys(value).length,
    });
  }

  return formatToolValue(value);
}

function readStringField(input: Record<string, unknown>, key: string): string | null {
  const value = input[key];
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

function getWebSearchSummary(call: ChatToolCall, t: TFunction): ToolCallSummary {
  const input = isRecord(call.input) ? call.input : {};
  const output = isRecord(call.output) ? call.output : {};
  const action = isRecord(output.action) ? output.action : {};
  const query = readStringField(input, 'query') ?? readStringField(action, 'query');
  const providedContextSize = readStringField(input, 'searchContextSize');
  const searchContextSize = providedContextSize ?? 'medium';

  return {
    headline: query
      ? t('modules.mavue2.chat.toolSummary.webSearchWithQuery', { query })
      : t('modules.mavue2.chat.toolSummary.webSearchDefault'),
    fields: [
      {
        label: t('modules.mavue2.chat.toolSummary.labels.query'),
        value: query ?? t('modules.mavue2.chat.toolSummary.values.notSet'),
        status: query ? 'Provided' : 'Unset',
        note: query
          ? t('modules.mavue2.chat.toolSummary.notes.publicWebQuery')
          : t('modules.mavue2.chat.toolSummary.notes.openAiControlsQuery'),
      },
      {
        label: t('modules.mavue2.chat.toolSummary.labels.searchDepth'),
        value: searchContextSize,
        status: providedContextSize ? 'Provided' : 'Defaulted',
        note: providedContextSize
          ? t('modules.mavue2.chat.toolSummary.notes.setFromToolInput')
          : t('modules.mavue2.chat.toolSummary.notes.defaultWebSearchContext'),
      },
      {
        label: t('modules.mavue2.chat.toolSummary.labels.dataScope'),
        value: t('modules.mavue2.chat.toolSummary.values.publicWeb'),
        status: 'Defaulted',
        note: t('modules.mavue2.chat.toolSummary.notes.publicWebScope'),
      },
    ],
  };
}

function getGenericToolSummary(call: ChatToolCall, t: TFunction): ToolCallSummary {
  const input = isRecord(call.input) ? call.input : null;
  const toolDisplayName = getToolDisplayName(call, t);

  if (!input) {
    const value = formatInlineValue(call.input, t);

    return {
      headline: t('modules.mavue2.chat.toolSummary.review', { tool: toolDisplayName }),
      fields: value
        ? [
            {
              label: t('modules.mavue2.chat.toolSummary.labels.input'),
              value,
              status: 'Provided',
              note: t('modules.mavue2.chat.toolSummary.notes.setFromRequest'),
            },
          ]
        : [],
    };
  }

  const fields = Object.entries(input).map(([key, value]): ToolSummaryField => {
    const formattedValue = formatInlineValue(value, t);

    return {
      label: key,
      value: formattedValue ?? t('modules.mavue2.chat.toolSummary.values.notSet'),
      status: formattedValue ? 'Provided' : 'Unset',
      note: formattedValue
        ? t('modules.mavue2.chat.toolSummary.notes.setFromRequest')
        : t('modules.mavue2.chat.toolSummary.notes.noDefensibleValue'),
    };
  });

  return {
    headline: t('modules.mavue2.chat.toolSummary.review', { tool: toolDisplayName }),
    fields,
  };
}

function getToolSummary(call: ChatToolCall, t: TFunction): ToolCallSummary {
  return getToolCallProvider(call) === ChatToolProvider.WEB_SEARCH
    ? getWebSearchSummary(call, t)
    : getGenericToolSummary(call, t);
}

function getToolSummaryStatusLabel(status: ToolSummaryField['status'], t: TFunction) {
  return t(TOOL_SUMMARY_STATUS_LABEL_KEYS[status]);
}

function getToolProviderApprovalLabel(call: ChatToolCall, t: TFunction) {
  return getToolCallProvider(call) === ChatToolProvider.WEB_SEARCH
    ? t('modules.mavue2.chat.toolNames.web_search')
    : t('modules.mavue2.chat.approval.providers.mavue');
}

function getToolProviderApprovalIcon(call: ChatToolCall) {
  return getToolCallProvider(call) === ChatToolProvider.WEB_SEARCH ? (
    <Globe2 className="h-4 w-4" />
  ) : (
    <ListTree className="h-4 w-4" />
  );
}

function formatApprovalPreviewValue(value: unknown, t: TFunction) {
  const formattedValue = formatToolValue(value);

  if (!formattedValue) {
    return t('modules.mavue2.chat.toolSummary.values.null');
  }

  return formattedValue.length > 180 ? `${formattedValue.slice(0, 177)}...` : formattedValue;
}

function getApprovalPreviewFields(input: unknown, t: TFunction) {
  if (!isRecord(input)) {
    return [
      {
        label: t('modules.mavue2.chat.toolSummary.labels.input'),
        value: formatApprovalPreviewValue(input, t),
      },
    ];
  }

  return Object.entries(input).map(([label, value]) => ({
    label,
    value: formatApprovalPreviewValue(value, t),
  }));
}

function isAnalyticsComponentPreview(value: unknown) {
  if (!isRecord(value)) {
    return false;
  }

  return (
    typeof value.title === 'string' &&
    typeof value.kind === 'string' &&
    isRecord(value.spec) &&
    Array.isArray(value.resolvedValues)
  );
}

function getAnalyticsPreviewValue(output: unknown): unknown[] {
  if (!isRecord(output)) {
    return [];
  }

  if (isAnalyticsComponentPreview(output)) {
    return [output];
  }

  if (Array.isArray(output.components)) {
    return output.components.filter(isAnalyticsComponentPreview);
  }

  if (Array.isArray(output.nodes)) {
    return output.nodes.filter(isAnalyticsComponentPreview);
  }

  return [];
}

function getAnalyticsDashboardPath(output: unknown) {
  if (!isRecord(output)) {
    return null;
  }

  const projectId = typeof output.projectId === 'string' ? output.projectId : null;
  const dashboardId =
    typeof output.dashboardId === 'string'
      ? output.dashboardId
      : typeof output.id === 'string' && Array.isArray(output.components)
        ? output.id
        : null;

  return projectId && dashboardId
    ? withProjectPrefix(`/analytics/${dashboardId}`, projectId)
    : null;
}

function getApprovalActionLabel(toolName: string, t: TFunction) {
  return ANALYTICS_APPROVAL_TOOL_NAMES.has(toolName)
    ? t(`modules.mavue2.chat.approvalActions.${toolName}`)
    : t('modules.mavue2.chat.approval.allow');
}

function RawToolDetailsDialog({ call, t }: { call: ChatToolCall; t: TFunction }) {
  const [open, setOpen] = useState(false);
  const input = formatToolValue(call.input);
  const output = formatToolValue(call.output);
  const toolDisplayName = getToolDisplayName(call, t);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-7 px-2 text-xs text-muted-foreground"
        onClick={() => setOpen(true)}
      >
        <Code2 className="mr-1.5 h-3.5 w-3.5" />
        {t('modules.mavue2.chat.toolDetails.viewRawDetails')}
      </Button>
      <DialogContent className="max-h-[80vh] max-w-3xl overflow-hidden">
        <DialogHeader>
          <DialogTitle>
            {t('modules.mavue2.chat.toolDetails.dialogTitle', { tool: toolDisplayName })}
          </DialogTitle>
        </DialogHeader>
        <div className="max-h-[62vh] space-y-3 overflow-auto">
          {input && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                {t('modules.mavue2.chat.toolDetails.input')}
              </div>
              <pre className="rounded-md bg-muted px-3 py-2 text-xs leading-5 text-muted-foreground [overflow-wrap:anywhere]">
                {input}
              </pre>
            </div>
          )}

          {output && (
            <div>
              <div className="mb-1 text-xs font-medium text-muted-foreground">
                {t('modules.mavue2.chat.toolDetails.output')}
              </div>
              <pre className="rounded-md bg-green-50 px-3 py-2 text-xs leading-5 text-green-950 [overflow-wrap:anywhere]">
                {output}
              </pre>
            </div>
          )}

          {call.errorText && (
            <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
              {call.errorText}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function ToolCallWorkLogDetails({
  call,
  disabled,
  onApproval,
  isActiveApproval,
  t,
}: {
  call: ChatToolCall;
  disabled: boolean;
  onApproval: (call: ChatToolCall, approved: boolean) => void;
  isActiveApproval: boolean;
  t: TFunction;
}) {
  const summary = getToolSummary(call, t);
  const canRespond = call.state === ChatToolCallState.APPROVAL_REQUESTED && !!call.approvalId;
  const canApprove = canRespond && isActiveApproval;
  const analyticsPreviews = getAnalyticsPreviewValue(call.output);
  const analyticsDashboardPath = getAnalyticsDashboardPath(call.output);

  if (canRespond && !isActiveApproval) {
    return (
      <div className="mt-2 rounded-lg border border-border bg-muted/20 px-3 py-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="text-sm text-muted-foreground">
            {t('modules.mavue2.chat.approval.queuedAfterPrevious')}
          </div>
          <RawToolDetailsDialog call={call} t={t} />
        </div>
      </div>
    );
  }

  if (canRespond && isActiveApproval) {
    const providerLabel = getToolProviderApprovalLabel(call, t);
    const toolDisplayName = getKnownToolDisplayName(call.toolName, t) ?? call.toolName;
    const previewFields = getApprovalPreviewFields(call.input, t);
    const approvalActionLabel = getApprovalActionLabel(call.toolName, t);

    return (
      <div className="mt-3 overflow-hidden rounded-xl border border-border bg-card shadow-sm">
        <div className="space-y-4 px-4 py-4">
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            {getToolProviderApprovalIcon(call)}
            <span>{providerLabel}</span>
          </div>

          <div className="text-base font-medium leading-7 text-foreground">
            {t('modules.mavue2.chat.approval.allowTool', {
              provider: providerLabel,
              toolName: toolDisplayName,
            })}
          </div>

          {previewFields.length > 0 && (
            <div className="space-y-2">
              {previewFields.map((field) => (
                <div
                  key={field.label}
                  className="grid gap-1 text-sm sm:grid-cols-[180px_1fr] sm:items-start"
                >
                  <div className="text-muted-foreground">{field.label}</div>
                  <div className="min-w-0 whitespace-pre-wrap break-words text-foreground">
                    {field.value}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-border bg-muted/20 px-4 py-3">
          <RawToolDetailsDialog call={call} t={t} />
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-9 text-muted-foreground"
            disabled={disabled}
            onClick={() => onApproval(call, false)}
          >
            <X className="mr-1.5 h-3.5 w-3.5" />
            {t('modules.mavue2.chat.approval.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            className="h-9 rounded-full px-4"
            disabled={disabled || !canApprove}
            onClick={() => onApproval(call, true)}
          >
            <Check className="mr-1.5 h-3.5 w-3.5" />
            {approvalActionLabel}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 rounded-md bg-muted/35 px-3 py-2">
      <p className="text-sm leading-6 text-foreground">{summary.headline}</p>

      {analyticsPreviews.length > 0 ? (
        <div className="mt-3 grid gap-3">
          {analyticsPreviews.slice(0, 2).map((preview, index) => (
            <AnalyticsChartRenderer key={index} component={preview as any} compact />
          ))}
        </div>
      ) : null}

      {summary.fields.length > 0 && (
        <div className="mt-2 divide-y divide-border/70">
          {summary.fields.map((field) => (
            <div
              key={field.label}
              className="grid gap-1 py-2 text-sm sm:grid-cols-[128px_1fr_auto] sm:items-start"
            >
              <div className="font-medium text-muted-foreground">{field.label}</div>
              <div className="min-w-0">
                <div className="break-words text-foreground">{field.value}</div>
                <div className="mt-0.5 text-xs leading-5 text-muted-foreground">{field.note}</div>
              </div>
              <span className="w-fit rounded-md border border-border bg-background px-2 py-0.5 text-xs text-muted-foreground">
                {getToolSummaryStatusLabel(field.status, t)}
              </span>
            </div>
          ))}
        </div>
      )}

      {call.errorText && (
        <div className="mt-2 flex gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span>{call.errorText}</span>
        </div>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {analyticsDashboardPath ? (
          <Button asChild type="button" variant="outline" size="sm" className="h-7 px-2 text-xs">
            <Link to={analyticsDashboardPath}>
              <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
              {t('modules.mavue2.analytics.actions.openDashboard')}
            </Link>
          </Button>
        ) : null}
        <RawToolDetailsDialog call={call} t={t} />
      </div>
    </div>
  );
}
